import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { api, configureApiClient } from '../services/api';
import type {
  User,
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  MeResponse,
  MfaChallengeMethod,
} from '../types/auth';

export interface PendingMfaChallenge {
  mfaToken: string;
  methods: MfaChallengeMethod[];
  expiresAt: string;
}

export interface MfaSetupBlock {
  reason: 'overdue';
  deadline: string | null;
  message: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // Set when login returns mfa_required: true. Caller routes the user to the
  // MFA challenge page, which calls completeMfaChallenge after submission.
  pendingMfaChallenge: PendingMfaChallenge | null;
  // Set when login returns mfa_setup_required: true with overdue=true. The
  // user can't get tokens until they enroll in MFA.
  mfaSetupBlock: MfaSetupBlock | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  completeMfaChallenge: () => Promise<void>;
  clearMfaChallenge: () => void;
  clearMfaSetupBlock: () => void;
  logout: () => Promise<void>;
  clearError: () => void;
  requestMagicLink: (email: string, captchaToken?: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  updateProfile: (input: { name: string }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingMfaChallenge, setPendingMfaChallenge] = useState<PendingMfaChallenge | null>(null);
  const [mfaSetupBlock, setMfaSetupBlock] = useState<MfaSetupBlock | null>(null);

  // Dedupes concurrent refresh calls. Refresh-token rotation is single-use,
  // so parallel callers (multi-tab, strict-mode re-invocations) would double-rotate.
  const inFlightRefreshRef = useRef<Promise<boolean> | null>(null);
  const didInitRef = useRef(false);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    if (inFlightRefreshRef.current) return inFlightRefreshRef.current;

    const promise = (async () => {
      try {
        const response = await api.post<{ success: boolean }>(
          '/api/auth/refresh-token',
          {},
          { skipAuth: true, skipRefresh: true }
        );
        return response.success === true;
      } catch {
        return false;
      }
    })();

    inFlightRefreshRef.current = promise;
    try {
      return await promise;
    } finally {
      inFlightRefreshRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
  }, []);

  useEffect(() => {
    configureApiClient({
      refreshSession,
      onLogout: handleLogout,
    });
  }, [refreshSession, handleLogout]);

  // Check for existing session on mount. If a valid cookie is present, /me returns
  // the user. If access token is expired, api.ts transparently refreshes via cookie.
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const initAuth = async () => {
      try {
        const meResponse = await api.get<MeResponse>('/api/auth/me');
        if (meResponse.success && meResponse.data) {
          setUser(meResponse.data.user);
        }
      } catch {
        // No valid session — user stays null, ProtectedRoute redirects to /login.
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const handleAuthResponse = useCallback((response: AuthResponse) => {
    if (response.mfa_required && response.data?.mfa_token) {
      setPendingMfaChallenge({
        mfaToken: response.data.mfa_token,
        methods: response.data.mfa_methods ?? ['totp'],
        expiresAt: response.data.expires_at ?? '',
      });
      setMfaSetupBlock(null);
      return 'challenge' as const;
    }
    if (response.success && response.data?.user) {
      setUser(response.data.user);
      setPendingMfaChallenge(null);
      setMfaSetupBlock(null);
      return 'authenticated' as const;
    }
    return 'unknown' as const;
  }, []);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setError(null);
      setIsLoading(true);

      try {
        const response = await api.post<AuthResponse>('/api/auth/login', credentials, {
          skipAuth: true,
        });

        const outcome = handleAuthResponse(response);
        if (outcome === 'authenticated' || outcome === 'challenge') return;

        // mfa_setup_required with status=403 lands here when the API blocks
        // the login outright (grace period expired). We surface a structured
        // signal so the LoginPage can render an enrollment screen.
        if (response.mfa_setup_required && response.mfa_setup_overdue) {
          setMfaSetupBlock({
            reason: 'overdue',
            deadline: response.data?.mfa_setup_deadline ?? null,
            message:
              response.error ||
              'Your organization requires multi-factor authentication. Set up MFA to continue.',
          });
          throw new Error(response.error || 'MFA enrollment required');
        }

        throw new Error(response.error || 'Login failed');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthResponse]
  );

  const completeMfaChallenge = useCallback(async () => {
    setError(null);
    try {
      const meResponse = await api.get<MeResponse>('/api/auth/me');
      if (meResponse.success && meResponse.data) {
        setUser(meResponse.data.user);
        setPendingMfaChallenge(null);
      } else {
        throw new Error(meResponse.error || 'Failed to load user after MFA verification');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load user';
      setError(message);
      throw err;
    }
  }, []);

  const clearMfaChallenge = useCallback(() => setPendingMfaChallenge(null), []);
  const clearMfaSetupBlock = useCallback(() => setMfaSetupBlock(null), []);

  const register = useCallback(async (credentials: RegisterCredentials) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await api.post<AuthResponse>('/api/auth/register', credentials, {
        skipAuth: true,
      });

      if (response.success && response.data?.user) {
        setUser(response.data.user);
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Used by the OAuth callback, which still receives tokens as URL params from the
  // provider redirect. Exchange them for cookies via refresh-token (which rotates
  // and Set-Cookies), then fetch the user via /me.
  const loginWithTokens = useCallback(async (_accessToken: string, refreshToken: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const refreshResponse = await api.post<{ success: boolean; error?: string }>(
        '/api/auth/refresh-token',
        { refresh_token: refreshToken },
        { skipAuth: true, skipRefresh: true }
      );

      if (!refreshResponse.success) {
        throw new Error(refreshResponse.error || 'OAuth login failed');
      }

      const meResponse = await api.get<MeResponse>('/api/auth/me');
      if (meResponse.success && meResponse.data) {
        setUser(meResponse.data.user);
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'OAuth login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Cookie is cleared server-side; tolerate network errors.
    } finally {
      handleLogout();
    }
  }, [handleLogout]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const requestMagicLink = useCallback(async (email: string, captchaToken?: string) => {
    setError(null);

    try {
      interface MagicLinkResponse {
        success: boolean;
        message: string;
        error?: string;
      }
      const response = await api.post<MagicLinkResponse>(
        '/api/auth/magic-link',
        { email, captcha_token: captchaToken },
        { skipAuth: true }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to send magic link');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send magic link';
      setError(message);
      throw err;
    }
  }, []);

  const verifyMagicLink = useCallback(
    async (token: string) => {
      setError(null);
      setIsLoading(true);

      try {
        const response = await api.post<AuthResponse>(
          '/api/auth/magic-link/verify',
          { token },
          { skipAuth: true }
        );

        const outcome = handleAuthResponse(response);
        if (outcome === 'authenticated' || outcome === 'challenge') return;

        throw new Error(response.error || 'Invalid magic link');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invalid magic link';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthResponse]
  );

  const requestOtp = useCallback(async (email: string) => {
    setError(null);

    try {
      interface OtpResponse {
        success: boolean;
        message: string;
        error?: string;
      }
      const response = await api.post<OtpResponse>('/api/auth/otp', { email }, { skipAuth: true });

      if (!response.success) {
        throw new Error(response.error || 'Failed to send verification code');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send verification code';
      setError(message);
      throw err;
    }
  }, []);

  const updateProfile = useCallback(async ({ name }: { name: string }) => {
    setError(null);
    const response = await api.patch<MeResponse>('/api/auth/me', { name });
    if (response.success && response.data?.user) {
      setUser(response.data.user);
      return;
    }
    const message = response.error || 'Failed to update profile';
    setError(message);
    throw new Error(message);
  }, []);

  const verifyOtp = useCallback(
    async (email: string, code: string) => {
      setError(null);
      setIsLoading(true);

      try {
        const response = await api.post<AuthResponse>(
          '/api/auth/otp/verify',
          { email, code },
          { skipAuth: true }
        );

        const outcome = handleAuthResponse(response);
        if (outcome === 'authenticated' || outcome === 'challenge') return;

        throw new Error(response.error || 'Invalid verification code');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Invalid verification code';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [handleAuthResponse]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        pendingMfaChallenge,
        mfaSetupBlock,
        login,
        register,
        loginWithTokens,
        completeMfaChallenge,
        clearMfaChallenge,
        clearMfaSetupBlock,
        logout,
        clearError,
        requestMagicLink,
        verifyMagicLink,
        requestOtp,
        verifyOtp,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
